import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import type { Offer } from '../../types';
import { OfferActions } from '../OfferActions/OfferActions';

interface GetColumnsOptions {
  onEdit?: (offer: Offer) => void;
  onViewDetails?: (offer: Offer) => void;
}

export const getOfferColumns = (options: GetColumnsOptions = {}): ColumnDef<Offer>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <div data-testid={`offer-id-${row.original.id}`} className="font-mono text-xs">
        {row.original.id}
      </div>
    ),
    size: 60,
  },
  {
    accessorKey: 'venue_name',
    header: 'Venue',
    cell: ({ row }) => (
      <div data-testid={`offer-venue-${row.original.id}`}>
        <p className="font-medium">{row.original.venue_name}</p>
        <p className="text-xs text-muted-foreground">{row.original.organization_name}</p>
      </div>
    ),
  },
  {
    accessorKey: 'short_description',
    header: 'Description',
    cell: ({ row }) => (
      <div data-testid={`offer-description-${row.original.id}`} className="max-w-md">
        {options.onViewDetails ? (
          <button
            className="text-left hover:underline"
            onClick={() => options.onViewDetails?.(row.original)}
          >
            <p className="truncate font-medium text-primary">{row.original.short_description}</p>
          </button>
        ) : (
          <p className="truncate">{row.original.short_description}</p>
        )}
      </div>
    ),
  },
  {
    accessorKey: 'claim_frequency',
    header: 'Claim Limit',
    cell: ({ row }) => (
      <div data-testid={`offer-claim-frequency-${row.original.id}`} className="text-center">
        {row.original.claim_frequency}
      </div>
    ),
    size: 100,
  },
  {
    accessorKey: 'farming_time_in_hours',
    header: 'Farming Time',
    cell: ({ row }) => {
      const hours = row.original.farming_time_in_hours;
      const displayText = hours === 0 ? 'Instant' : `${hours}h`;
      return (
        <div data-testid={`offer-farming-time-${row.original.id}`} className="text-center">
          {displayText}
        </div>
      );
    },
    size: 120,
  },
  {
    accessorKey: 'expires_at',
    header: 'Expires',
    cell: ({ row }) => {
      const expiryDate = new Date(row.original.expires_at);
      const isExpired = expiryDate < new Date();
      const formattedDate = expiryDate.toLocaleDateString();
      
      return (
        <div data-testid={`offer-expiry-${row.original.id}`}>
          <span className={isExpired ? 'text-destructive' : ''}>
            {formattedDate}
          </span>
          {isExpired && (
            <Badge variant="destructive" className="ml-2 text-xs">
              Expired
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'is_active',
    header: 'Status',
    cell: ({ row }) => {
      const isActive = row.original.is_active;
      const isExpired = new Date(row.original.expires_at) < new Date();
      
      return (
        <Badge
          variant={isActive && !isExpired ? 'default' : 'secondary'}
          data-testid={`offer-status-${row.original.id}`}
          className={isActive && !isExpired ? 'bg-green-100 text-green-800' : ''}
        >
          {isExpired ? 'Expired' : isActive ? 'Active' : 'Inactive'}
        </Badge>
      );
    },
  },
  {
    id: 'claims',
    header: 'Claims',
    cell: ({ row }) => (
      <div data-testid={`offer-claims-${row.original.id}`} className="text-center text-sm">
        <div>{row.original.total_claims} total</div>
        <div className="text-xs text-muted-foreground">
          {row.original.active_claims} active
        </div>
      </div>
    ),
    size: 100,
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <OfferActions 
        offer={row.original} 
        onEdit={options.onEdit}
      />
    ),
    size: 50,
    meta: {
      align: 'right',
    },
  },
];